import { useEffect, useMemo, useRef, useState } from 'react'
import { datadogLogs } from '@datadog/browser-logs'
import { emitRoundBettingClosed } from 'src/services/postMessage/emitters'
import { claimBettingClosedPost, raceEventHeardAt } from 'src/store/raceEventHeard'
import { DEFAULT_LOCK_LEAD_S } from 'src/types/domain/HardClose'
import type { Race } from 'src/types/domain/Race'
import {
  countdownLockAt,
  createBetLock,
  type BetLock,
  type BetLockFired,
  type LockSignal
} from 'src/utils/betLock'
import { roundHardClose } from 'src/utils/hardCloseClock'

export interface BetLockSent extends BetLockFired {
  roundId: string
  /** X, the lead the round's engine armed `race_starting` with. */
  lockLeadS: number
}

export interface BetLockView {
  /** The round on the board runs the hard close. False: today's board, untouched. */
  hard: boolean
  roundId: string | undefined
  /** X as the board reads it (4 on a round that is not hard). */
  lockLeadS: number
  /** The countdown's end, `performance.now()` ms; undefined without one. */
  lockAt: number | undefined
  /** When the board locked, `performance.now()` ms; undefined before (and for `joined`). */
  lockedAt: number | undefined
  /**
   * What locked it: `race_starting`'s arrival, the countdown, or `joined` (the
   * page came in after `race_starting`: nothing is sent to that round).
   */
  lockedBy: LockSignal | 'joined' | undefined
}

const NOT_HARD: BetLockView = {
  hard: false,
  roundId: undefined,
  lockLeadS: DEFAULT_LOCK_LEAD_S,
  lockAt: undefined,
  lockedAt: undefined,
  lockedBy: undefined
}

/**
 * The one lock every hard-round board uses (DESIGN §6): ChipPlacementPanel,
 * BirdieBoard, CrashChipPanel and PlaceBetPanel.
 *
 * On a hard round it locks at the FIRST of `race_starting`'s arrival and the
 * board's own countdown (heardAt(`race_ready`) + (W − X), `performance.now()`),
 * and calls `onLock` once, at that moment: the board sends every option then,
 * empty or not, and never again that round. `round:betting-closed` is posted
 * at the same moment. A round that is not hard gets `hard: false`, no timer
 * and no call: its board runs today's code.
 *
 * `onLock` is read through a ref, so a board's callback may change identity on
 * every render without re-arming anything.
 */
export function useBetLock(input: {
  race: Race | undefined
  previousRace?: Race | undefined
  onLock(sent: BetLockSent): void
}): BetLockView {
  const { race, previousRace } = input
  const onLockRef = useRef(input.onLock)
  onLockRef.current = input.onLock

  const roundId = race?.uuid
  const gameId = race?.raceConfigurationId
  const hardClose = useMemo(() => roundHardClose(race, previousRace), [race, previousRace])
  const hard = hardClose !== null
  const lockLeadS = hardClose?.close.lockLeadS ?? DEFAULT_LOCK_LEAD_S
  // The countdown only for a round whose own `race_ready` this page HEARD.
  const readyHeardAt = raceEventHeardAt(roundId, 'race_ready')
  const lockAt =
    hardClose?.from === 'round' ? countdownLockAt(readyHeardAt, hardClose.close) : undefined
  const startingPresent = race?.raceEvents.some((e) => e.eventType === 'race_starting') ?? false
  const startingHeard = startingPresent && raceEventHeardAt(roundId, 'race_starting') !== undefined

  const [fired, setFired] = useState<BetLockSent | null>(null)
  const machineRef = useRef<{ roundId: string; lock: BetLock } | null>(null)
  /** The rounds this board has locked already: a re-armed machine never fires for them again. */
  const lockedRoundRef = useRef<string | undefined>(undefined)
  /** The per-round log line (DESIGN §6 "Logs"), written once a round. */
  const loggedRoundRef = useRef<string | undefined>(undefined)

  useEffect(() => {
    if (!roundId || !hard) return undefined
    // Joined after `race_starting`: the round is closed to this board.
    if (startingPresent && !startingHeard) return undefined
    if (lockedRoundRef.current === roundId) return undefined
    const lock = createBetLock({
      lockAt,
      onLock(lockFired) {
        lockedRoundRef.current = roundId
        const sent: BetLockSent = { ...lockFired, roundId, lockLeadS }
        setFired(sent)
        if (gameId && claimBettingClosedPost(roundId)) {
          emitRoundBettingClosed({ roundId, gameId })
        }
        onLockRef.current(sent)
      }
    })
    machineRef.current = { roundId, lock }
    return () => {
      lock.dispose()
      if (machineRef.current?.lock === lock) machineRef.current = null
    }
    // `startingHeard` is read at arming only; its arrival is the effect below.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roundId, hard, lockAt, lockLeadS, gameId])

  // `race_starting` heard live: lock now, unless the countdown already did.
  useEffect(() => {
    if (!startingHeard || !roundId) return
    const machine = machineRef.current
    if (machine?.roundId === roundId) machine.lock.starting()
  }, [startingHeard, roundId, hard])

  // One line per hard round: which signal locked, and how far `race_starting`
  // landed from the countdown's end (heardAt(starting) − heardAt(ready) − (W − X)).
  useEffect(() => {
    if (!hard || !roundId || !startingHeard || loggedRoundRef.current === roundId) return
    if (fired?.roundId !== roundId) return
    loggedRoundRef.current = roundId
    const startingAt = raceEventHeardAt(roundId, 'race_starting')
    datadogLogs.logger.info('hard_close_lock', {
      user_event: {
        roundId,
        gameId,
        lockedBy: fired.signal,
        frozen: fired.frozen,
        readyToStartingMs:
          startingAt !== undefined && readyHeardAt !== undefined
            ? Math.round(startingAt - readyHeardAt)
            : undefined,
        startingAfterCountdownMs:
          startingAt !== undefined && lockAt !== undefined
            ? Math.round(startingAt - lockAt)
            : undefined
      }
    })
  }, [fired, gameId, hard, lockAt, readyHeardAt, roundId, startingHeard])

  if (!hard || !roundId) return NOT_HARD
  const own = fired?.roundId === roundId ? fired : null
  return {
    hard,
    roundId,
    lockLeadS,
    lockAt,
    lockedAt: own?.at,
    lockedBy: own ? own.signal : startingPresent && !startingHeard ? 'joined' : undefined
  }
}
